import { WatchfaceService } from '@/services/watchface';
import { defineStore } from 'pinia';
import { LayerUtils } from '@/utils/shape/layer.util';
import { ShapeUtils } from '@/services/shape/shape-util';
import { Shape, ShapeType } from '@/services/shape';
import KonvaService from '@/services/konva';
import { useIFTTTStore } from './use-ifttt-store';

export const useLayerStore = defineStore('layer-store', {
  state: () => ({
    themes: [] as StoreThemeItem[],
    /** 息屏展示模式：single 单息屏，multiColor 多配色息屏 */
    aodDisplayMode: 'single' as AodDisplayMode
  }),
  getters: {
    findTheme(state) {
      return (id: string): StoreThemeItem | undefined => {
        return state.themes.find((item) => item.id === id);
      };
    },
    anotherTheme(state) {
      /** 返回息屏对应的亮屏，或亮屏对应的息屏theme */
      return (theme: StoreThemeItem): StoreThemeItem | undefined => {
        const type = theme.type == 'AOD' ? 'normal' : 'AOD';
        return state.themes.find(
          (item) => item.name === theme.name && item.type == type
        );
      };
    },

    /**
     * 查找layer
     * @param state
     * @returns
     */
    findLayer(state) {
      return (
        id: string | ((layer: StoreLayerItem) => boolean),
        theme?: StoreThemeItem
      ) => {
        const _find = (
          layers: StoreLayerItem[],
          parent: StoreLayerItem | StoreThemeItem
        ): FindLayerResult | undefined => {
          for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            if (typeof id == 'function' ? !!id?.(layer) : layer.id == id) {
              return {
                layer,
                parent,
                index: i
              };
            }

            if (layer.children?.length) {
              const res = _find(layer.children, layer);
              if (res) return res;
            }
          }
        };

        const themes = theme ? [theme] : state.themes;
        for (let i = 0; i < themes.length; i++) {
          const theme = themes[i];
          const res = _find(theme.children, theme);
          if (res) {
            return res;
          }
        }
      };
    },

    /**
     * 是否为单息屏
     * @param state
     * @returns
     */
    isSingleAOD(state) {
      return state.aodDisplayMode === 'single';
    },
    /**
     * 是否为多配色息屏
     * @param state
     * @returns
     */
    isMultiColorAOD(state) {
      return state.aodDisplayMode === 'multiColor';
    },
    /**
     * 获取用于 UI 展示的 themes，按 type 过滤；单息屏模式下 AOD 只展示第一个 normal 对应的
     */
    displayThemesForType(state) {
      return (type: 'normal' | 'AOD'): StoreThemeItem[] => {
        let themes = state.themes.filter((item) => item.type === type);
        if (type === 'AOD' && state.aodDisplayMode === 'single') {
          const firstNormal = state.themes.find((t) => t.type === 'normal');
          if (firstNormal) {
            themes = themes.filter((t) => t.name === firstNormal.name);
          }
        }
        return themes;
      };
    },
    themeLabelName(state) {
      return (themeId: string) => {
        const theme = state.themes.find((item) => item.id == themeId);
        if (!theme) return '-';
        if (theme.type == 'normal') return theme.name;

        // 查找对应的亮屏
        const normalTheme = state.themes.find(
          (item) => item.name == theme.name && item.type == 'normal'
        );
        if (!normalTheme) return '-';
        if (state.themes.filter((item) => item.type == 'AOD').length < 2)
          return '息屏';
        return normalTheme.name + '的息屏';
      };
    },
    getSpriteAndVideoWatchfaceIds(state) {
      return (themeId: string) => {
        const ids: string[] = [];
        const _find = (shapes: StoreLayerItem[]) => {
          shapes.forEach((item) => {
            if (
              item.type == ShapeType.Sprite ||
              item.type == ShapeType.VideoWatchface
            ) {
              ids.push(item.id);
            } else if (item.children?.length) {
              _find(item.children);
            }
          });
        };
        _find(state.themes.find((item) => item.id == themeId)?.children ?? []);
        return ids;
      };
    }
  },
  actions: {
    addLayer({
      layer,
      themeId,
      parentId,
      index = 0
    }: {
      layer: StoreLayerItem;
      themeId: string;
      parentId?: string;
      index?: number;
    }) {
      if (parentId) {
        const { layer: parentLayer } = this.findLayer(parentId) ?? {};
        parentLayer?.children?.splice(index, 0, layer);
        return;
      }
      const theme = this.findTheme(themeId);
      theme?.children?.splice(index, 0, layer);
    },
    moveLayer(id: string, themeId: string, parentId?: string, destIndex = 0) {
      const { layer, index, parent } = this.findLayer(id) || {};
      if (!layer || index === undefined) {
        console.error(`样式：${themeId} 中找不到图层：${id}`);
        return;
      }
      let targetParent: StoreLayerItem | undefined;
      if (parentId) {
        const target = this.findLayer(parentId);
        if (!target) {
          console.error(`样式：${themeId} 中找不到父节点：${parentId}`);
          return;
        }
        targetParent = target.layer;
      }

      // 判断是否为同父级内的排序（仅改变 z-order，不改变坐标）
      // 情况1：targetParent 存在且与当前 parent 是同一个 StoreLayerItem（组内排序）
      // 情况2：targetParent 为空且当前 parent 为 StoreThemeItem（根级别排序）
      const theme = this.findTheme(themeId);

      const isSameParentReorder =
        targetParent &&
        parent &&
        'id' in parent &&
        targetParent.id === (parent as StoreLayerItem).id;

      // 根级别排序：targetParent 为空，且当前元素在根级别（parent 为 theme）
      const isRootLevelReorder = !targetParent && parent === theme;

      // 同父级排序时无需计算坐标，直接移动即可
      if (isSameParentReorder) {
        (parent as StoreLayerItem).children?.splice(index, 1);
        targetParent!.children?.splice(destIndex, 0, layer);
        return layer;
      }

      // 根级别排序：仅改变 z-order，不改变坐标
      if (isRootLevelReorder && theme) {
        theme.children.splice(index, 1);
        theme.children.splice(destIndex, 0, layer);
        return layer;
      }

      // 以下为跨父级移动逻辑，需要重新计算坐标

      // 记录移动之前的绝对位置
      const service = KonvaService.getIns();
      const { x: x0 = 0, y: y0 = 0 } = service?.getClientRect(layer.id) ?? {};

      // 修复：在删除元素之前，先获取目标父元素的坐标（避免画布状态不一致）
      let targetParentAbsoluteX = 0;
      let targetParentAbsoluteY = 0;
      if (targetParent) {
        const targetParentShape = Shape.getShape(targetParent.id);
        const isTargetParentInSlot =
          targetParentShape?.parent?.type === ShapeType.Slot;

        if (isTargetParentInSlot) {
          // 如果目标父元素在容器中，使用 attrs 中的相对坐标 + 容器的绝对坐标
          const targetParentX = Number(targetParent.attrs.x) || 0;
          const targetParentY = Number(targetParent.attrs.y) || 0;

          // 获取容器（Slot）的绝对坐标
          const slotShape = targetParentShape?.parent;
          const slotRect = slotShape
            ? service?.getClientRect(slotShape.id)
            : undefined;
          const slotX = slotRect ? Number(slotRect.x) || 0 : 0;
          const slotY = slotRect ? Number(slotRect.y) || 0 : 0;

          // 计算目标父元素（组）的绝对坐标 = 容器绝对坐标 + 组相对坐标
          targetParentAbsoluteX = slotX + targetParentX;
          targetParentAbsoluteY = slotY + targetParentY;
        } else {
          // 目标父元素不在容器中，直接获取绝对坐标
          const rect = service?.getClientRect(targetParent.id);
          if (rect) {
            targetParentAbsoluteX = Number(rect.x) || 0;
            targetParentAbsoluteY = Number(rect.y) || 0;
          } else {
            targetParentAbsoluteX = 0;
            targetParentAbsoluteY = 0;
          }
        }
      }

      // 先在原来的父元素中删除
      if (parent) {
        // layer.attrs.x += parent.attrs.x;
        // layer.attrs.y += parent.attrs.y;
        parent.children?.splice(index, 1);
      } else {
        theme?.children.splice(index, 1);
      }

      if (targetParent) {
        // 向Slot中移动
        if (targetParent.type == ShapeType.Slot) {
          //（仅有widget可以移动到slot中），则需要将layer的x,y设置为0
          layer.attrs.x = layer.attrs.y = 0;
        } else {
          // 向组或widget中移动
          // 使用之前计算好的目标父元素绝对坐标
          layer.attrs.x = x0 - targetParentAbsoluteX;
          layer.attrs.y = y0 - targetParentAbsoluteY;

          // 防御性处理：确保坐标不为负值
          // 如果计算结果为负值，可能是计算错误，使用0作为最小值
          if (isNaN(layer.attrs.x) || layer.attrs.x < 0) {
            layer.attrs.x = 0;
          }
          if (isNaN(layer.attrs.y) || layer.attrs.y < 0) {
            layer.attrs.y = 0;
          }
        }
        targetParent.children?.splice(destIndex, 0, layer);
      } else {
        // 向根目录插入
        layer.attrs.x = x0;
        layer.attrs.y = y0;
        theme?.children.splice(destIndex, 0, layer);
      }

      return layer;
    },
    group(
      shapeIds: string[],
      newLayer: StoreLayerItem,
      themeId: string,
      parentId?: string
    ) {
      // 重置newLayer的name
      newLayer.attrs.name = ShapeUtils.generateShapeName(
        newLayer.type as ShapeType,
        parentId
      );
      // 将shapeIds倒序排列
      const _shapeIds: string[] = [];
      for (let i = shapeIds.length - 1; i >= 0; i--) {
        _shapeIds.push(shapeIds[i]);
      }

      const theme = this.findTheme(themeId) as StoreThemeItem;
      const parentList = parentId
        ? this.findLayer(parentId)?.layer.children || []
        : theme?.children;
      const children = _shapeIds.map((id) =>
        parentList.find((item) => item.id == id)
      ) as StoreLayerItem[];

      // 成组时用 store attrs 计算包围盒，与子元素坐标系一致（相对共同父级），
      // 避免 getClientRect 返回画布绝对坐标导致 setRelativeAttrs 后子元素相对位置错误
      const layerRange = LayerUtils.calcLayersRectFromAttrs(children, true);
      let first = Infinity;
      children.forEach((layer) => {
        const curIndex = parentList.findIndex((item) => item.id === layer.id);
        const delLayer = parentList.splice(curIndex, 1)[0];
        newLayer.children?.push(delLayer);
        LayerUtils.setRelativeAttrs(delLayer, layerRange);
        first = Math.min(first, curIndex);
      });

      Object.assign(newLayer.attrs, layerRange);
      parentList.splice(first, 0, newLayer);
      return newLayer;
    },
    ungroup(id: string, layers: StoreLayerItem[], themeId: string) {
      const theme = this.findTheme(themeId);
      if (!theme) {
        console.warn(`theme: ${themeId} 找不到`);
        return;
      }
      const target = this.findLayer(id);
      if (!target) return;
      const { layer, index, parent } = target;
      const parentList = parent ? parent.children || [] : theme.children;

      if (layer?.children?.length) {
        parentList.splice(index, 1, ...layers);
      } else {
        parentList.splice(index, 1);
      }
    },

    /**
     * 读取本地json文件或themes到store数据中
     * @param configOrThemes 可传入 StoreEditorConfig 或 themes 数组；不传则从本地读取
     */
    async sync(configOrThemes?: StoreEditorConfig | StoreThemeItem[]) {
      const _deriveAodDisplayMode = (
        themes: StoreThemeItem[]
      ): AodDisplayMode => {
        const aodThemes = themes.filter((t) => t.type === 'AOD');
        // 显式标记优先
        if (aodThemes.some((t) => t.isSingleAOD === false)) return 'multiColor';
        if (aodThemes.some((t) => t.isSingleAOD === true)) return 'single';
        // 老版本兼容：多 AOD 即多配色（旧 config 无 aodDisplayMode 且 theme 无 isSingleAOD）
        if (aodThemes.length > 1) return 'multiColor';
        return 'single';
      };

      if (configOrThemes) {
        if (Array.isArray(configOrThemes)) {
          this.themes = configOrThemes;
          // 仅 themes 数组时保留 aodDisplayMode 不变（如 correctLayerStore 预处理），避免覆盖已从 config 读取的值
        } else {
          this.themes = configOrThemes.themes ?? [];
          this.aodDisplayMode =
            configOrThemes.aodDisplayMode ?? _deriveAodDisplayMode(this.themes);
        }
      } else {
        const res = await new WatchfaceService().editorConfig.getEditorData();
        this.themes = res?.themes ?? [];
        this.aodDisplayMode =
          res?.aodDisplayMode ?? _deriveAodDisplayMode(this.themes);
      }
    }
  }
});
